import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "primereact/button";
import * as styles from "./ForklyWidget.module.css";
import useChatBot from "./useChatBot";
import useVoiceCommands from "./useVoiceCommands";
import {
  getForklyStepView,
  labelForForklyChip,
  FORKLY_PHRASES,
} from "./forklySteps";
import ForklyMessages from "./ForklyMessages";
import ForklyDock from "./ForklyDock";
import useFabPeek from "./useFabPeek";
import Confetti from "../Confetti";
import ForklyIcon from "../../assets/forkly_icon.svg";

const FORKLY_VOICE = {
  getStepView: getForklyStepView,
  labelForChip: labelForForklyChip,
  phrases: FORKLY_PHRASES,
};

const CLOSE_DURATION = 200;

const ForklyWidget = ({ sendViaWS }) => {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const appLoading = useSelector((s) => s.app.loading);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fabRef = useRef(null);

  const {
    expanded: fabExpanded,
    wiggle: fabWiggle,
    reset: resetFab,
  } = useFabPeek(open, appLoading);

  const {
    messages,
    chips,
    items,
    input,
    multi,
    multiMax,
    loading,
    typing,
    waiting,
    inputError,
    stepId,
    isDone,
    voiceMode,
    enableVoiceMode,
    handleChipClick,
    handleVoiceMultiSelect,
    handleTextSubmit,
    restart,
  } = useChatBot(open, sendViaWS, FORKLY_VOICE);

  const [inputValue, setInputValue] = useState("");

  const busy = loading || typing || waiting;
  const showChips = chips.length > 0 && !loading && !waiting;
  const showItems = items.length > 0 && !loading && !waiting;
  const showInput = !!input && !loading && !waiting;

  const fillInput = useCallback((text) => {
    setInputValue(text);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const {
    voiceState,
    transcript,
    error: voiceError,
    startRecording,
    stopRecording,
    reset: resetVoice,
    showMicButton,
    inSession,
    startSession,
    heardValue,
    pendingMatch,
    confirmMatch,
    dismissMatch,
  } = useVoiceCommands({
    busy,
    showInput,
    showItems,
    showChips,
    multi,
    items,
    chips,
    stepId,
    voiceMode,
    onEnableVoiceMode: enableVoiceMode,
    handleChipClick,
    handleVoiceMultiSelect,
    onFillInput: fillInput,
  });

  const optionsHidden = inSession && !showInput;
  const voiceOptions = (showItems ? items : showChips ? chips : []).filter(
    (o) => !(o.value === "__done__" || o.action === "done"),
  );

  useEffect(() => {
    setInputValue(input?.initialValue ?? "");
  }, [stepId]);

  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open, messages.length, loading, typing]);

  const handleInputChange = (e) => {
    let v = e.target.value;
    if (input?.digitsOnly) v = v.replace(/\D/g, "");
    if (input?.maxLength && v.length > input.maxLength) {
      v = v.slice(0, input.maxLength);
    }
    setInputValue(v);
  };

  const submitText = () => {
    if (input && inputValue.trim()) handleTextSubmit(inputValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submitText();
    }
  };

  useEffect(() => {
    if (open) {
      setClosing(false);
      setVisible(true);
    } else if (visible) {
      setClosing(true);
      const t = setTimeout(() => {
        setVisible(false);
        setClosing(false);
      }, CLOSE_DURATION);
      return () => clearTimeout(t);
    }
  }, [open]);

  const toggleOpen = () => {
    setOpen((v) => !v);
    resetFab();
  };

  const showDock = showInput || showChips || showItems;

  return (
    <div className={styles.root}>
      {isDone && (
        <Confetti
          originRef={fabRef}
          spread={360}
          startVelocity={28}
          particleCount={120}
        />
      )}
      {visible && (
        <div
          className={`${styles.window} ${closing ? styles.windowClosing : ""}`}
          role="dialog"
          aria-label="Forkly quick order"
        >
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <img src={ForklyIcon} alt="Forkly" className={styles.avatar} />
              <span className={styles.brand}>
                <span className={styles.brandName}>Forkly</span>
                <span className={styles.brandSub}>No line, no wait.</span>
              </span>
            </div>
            <Button
              unstyled
              className={styles.headerBtn}
              icon="pi pi-refresh"
              onClick={() => restart()}
              aria-label="New session"
              title="New session"
            />
          </div>

          <ForklyMessages
            messages={messages}
            busy={busy}
            endRef={messagesEndRef}
          />

          {showDock && (
            <ForklyDock
              showInput={showInput}
              showItems={showItems}
              showChips={showChips}
              input={input}
              inputRef={inputRef}
              inputValue={inputValue}
              loading={loading}
              chips={chips}
              items={items}
              multi={multi}
              multiMax={multiMax}
              stepId={stepId}
              inputError={inputError}
              onInputChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onSubmit={submitText}
              onChip={handleChipClick}
              voiceState={voiceState}
              transcript={transcript}
              voiceError={voiceError}
              showMicButton={showMicButton}
              onMicStart={startRecording}
              onMicStop={stopRecording}
              onMicReset={resetVoice}
              inSession={inSession}
              onStartSession={startSession}
              optionsHidden={optionsHidden}
              voiceOptions={voiceOptions}
              heardValue={heardValue}
              pendingMatch={pendingMatch}
              onConfirmMatch={confirmMatch}
              onDismissMatch={dismissMatch}
            />
          )}
        </div>
      )}

      <button
        ref={fabRef}
        className={`${styles.fab} ${open ? styles.fabOpen : ""} ${!open && fabExpanded ? styles.fabExpanded : ""} ${!open && fabWiggle ? styles.fabWiggle : ""}`}
        onClick={toggleOpen}
        aria-label={open ? "Close Forkly" : "Quick order with Forkly"}
      >
        {open ? (
          <i className="pi pi-times" style={{ color: "#fff", fontSize: 20 }} />
        ) : (
          <>
            <span className={styles.fabFork}>
              <img src={ForklyIcon} alt="" className={styles.fabIcon} />
              <span className={styles.fabSpark} />
            </span>
            <span className={styles.fabLabel}>
              Order even faster with <strong>Forkly</strong>
            </span>
          </>
        )}
      </button>
    </div>
  );
};

export default ForklyWidget;
